import {
  type CreateHillChartBody,
  type CreateSnapshotBody,
  type UpdateDotBody,
  type UpdateHillBody,
  dotSchema,
  externalProjectsListResponseSchema,
  hillChartListResponseSchema,
  hillChartSchema,
  snapshotSchema,
  snapshotsListResponseSchema,
  userSchema,
} from '@hillchart/shared';
import { z } from 'zod';

/**
 * Typed fetch wrapper for the Hills backend API.
 *
 * Every response is Zod-validated so a backend schema drift surfaces at the
 * boundary, not deep in a component tree. Resource-specific clients (auth,
 * hills, snapshots) sit on top of `request` and expose the wire contract to
 * the UI as Promises returning inferred types from `@hillchart/shared`.
 *
 * All requests are credentialed: the GitLab OAuth session cookie is how the
 * backend authenticates, so we need `credentials: 'include'` even in dev
 * where the frontend (5173) and backend (3001) sit on different origins.
 */
const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/**
 * Client-side knobs on a single call. Distinct from `RequestInit`, which
 * describes the wire request: these change how `request` reacts to the
 * response, not what is sent.
 */
interface RequestOptions {
  /**
   * Opt this call out of the global GITLAB_TOKEN_EXPIRED -> OAuth redirect
   * below. Default `false`: every call redirects unless it explicitly says
   * otherwise, so forgetting the flag can only ever be the safe way round.
   *
   * Reserved for requests the user did not ask for. Never set it on a call
   * behind a button.
   */
  suppressAuthRedirect?: boolean;
}

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  init?: RequestInit,
  options?: RequestOptions,
): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
    };
    const error = new ApiError(res.status, body.error ?? 'UNKNOWN', body.message ?? res.statusText);

    // Auto-recover from an expired GitLab OAuth token by sending the
    // user straight through the sign-in flow again. By the time they come
    // back the new sealed token is in the DB, the next request
    // succeeds, and the OAuth callback lands them back in the app.
    //
    // We still throw so the calling code (TanStack Query mutations)
    // stops cleanly. The navigation is a side-effect on `window.location`
    // that resolves on the next event-loop tick - the throw fires
    // synchronously, so React Query enters its error state but the
    // navigation supersedes any error UI before it paints.
    //
    // The `window` guard keeps the helper SSR-safe and lets unit tests
    // run in environments without a DOM (none today, but defensive).
    //
    // `suppressAuthRedirect` exists because that reasoning only holds for a
    // request the user initiated. They clicked Sync, it failed because their
    // session aged out, so sending them to sign in again is the answer to
    // what they asked for. A request fired in the background by simply
    // opening a page is different: the user asked for the page, and
    // navigating them into an OAuth flow they never triggered is a hijack,
    // not a recovery. Such calls opt out and let their own `onError` decide
    // (today: swallow it silently). The only caller is
    // `hillsApi.resyncInBackground`.
    if (
      error.code === 'GITLAB_TOKEN_EXPIRED' &&
      options?.suppressAuthRedirect !== true &&
      typeof globalThis.window !== 'undefined'
    ) {
      globalThis.window.location.href = `${API_URL}/auth/gitlab/login`;
    }

    throw error;
  }

  if (res.status === 204) return undefined as T;

  const json: unknown = await res.json();
  return schema.parse(json);
}

/**
 * Shared success envelope for endpoints that only need to signal "done".
 * Backend convention for DELETE + logout is `200 { ok: true }`. Keep in sync
 * with `sharedOkResponseSchema` on the backend.
 */
const okSchema = z.object({ ok: z.literal(true) });

// -- Auth --------------------------------------------------------------------

export const authApi = {
  /** Redirect target for the OAuth consent screen. Used with window.location. */
  gitlabLoginUrl: (): string => `${API_URL}/auth/gitlab/login`,
  /** Returns the currently authenticated user. Throws 401 if no session. */
  me: () => request('/auth/me', userSchema),
  /** Clears the session cookie. No-op if already logged out. */
  logout: () => request('/auth/logout', okSchema, { method: 'POST' }),
};

// -- Hills -------------------------------------------------------------------
type HillListStatus = 'active' | 'done' | 'all';

export const hillsApi = {
  /** GitLab projects that can be imported as hill charts. */
  gitlabProjects: () => request('/api/gitlab/projects', externalProjectsListResponseSchema),
  /**
   * Hill charts filtered by lifecycle state. Default `active` matches the
   * backend default and keeps the home page focused on live work; the
   * archive view passes `done` to surface completed hills.
   */
  list: (status: HillListStatus = 'active') =>
    request(`/api/hills?status=${status}`, hillChartListResponseSchema),
  /** One hill + its dots. */
  get: (id: string) => request(`/api/hills/${id}`, hillChartSchema),
  /**
   * Import a GitLab project as a new hill chart. Idempotent on
   * `externalProjectId` - a second call for the same project returns the
   * existing hill with 200 rather than creating a duplicate.
   */
  create: (body: CreateHillChartBody) =>
    request('/api/hills', hillChartSchema, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /**
   * Delete a hill (active or done). Cascades to dots and snapshots
   * server-side and broadcasts `hill:deleted` + closes the WS room with
   * code 4410 so any connected client lands on a clean empty state.
   *
   * Backend responds `200 { ok: true }` rather than `204 No Content`, so we
   * parse the envelope - `z.void()` would throw on the payload and surface
   * as a mutation error even though the DELETE itself succeeded.
   */
  delete: (id: string) =>
    request(`/api/hills/${id}`, okSchema, {
      method: 'DELETE',
    }),
  /**
   * Mark a hill as Done. Atomic on the backend: takes a `kind='final'`
   * snapshot in the same transaction as the state update, then broadcasts
   * `hill:completed` and closes the WS room with code 4423. Idempotent -
   * a second call on a hill that's already Done returns the same payload.
   * Subsequent dot moves / snapshot captures / resync calls return 409
   * HILL_COMPLETED.
   */
  complete: (id: string) =>
    request(`/api/hills/${id}/complete`, hillChartSchema, {
      method: 'POST',
    }),
  /**
   * Diff local dots against GitLab milestones and apply identity fixes.
   * Active hills only - returns 409 HILL_COMPLETED on a Done hill, and 409
   * LEGACY_PROVIDER on a hill imported from Linear before this migration
   * (its upstream project no longer exists to sync against).
   */
  resync: (id: string) =>
    request(`/api/hills/${id}/resync`, hillChartSchema, {
      method: 'POST',
    }),
  /**
   * Same endpoint as `resync`, for the reconcile that fires on its own when
   * an active GitLab hill is opened. The user did not ask for it and nothing
   * in the UI is bound to it, so it is the one call in this client that opts
   * out of the GITLAB_TOKEN_EXPIRED redirect: a token that aged out while the
   * page was loading must not throw the user into a sign-in flow they never
   * started. It still rejects, so the caller's `onError` runs.
   *
   * A separate method rather than a flag on `resync` so the exemption cannot
   * be reached by accident: the manual Sync button calls `resync` and keeps
   * the redirect, which is correct for an action the user clicked.
   */
  resyncInBackground: (id: string) =>
    request(
      `/api/hills/${id}/resync`,
      hillChartSchema,
      { method: 'POST' },
      { suppressAuthRedirect: true },
    ),
  /**
   * Rename the hill. `title` is user-owned: it is seeded from the upstream
   * project name at import time and resync never touches it afterwards, so
   * this is the only writer. `externalProjectName` keeps mirroring upstream,
   * which is why the two are allowed to diverge.
   *
   * Returns the full updated hill so the caller can paint the detail cache
   * without a refetch. Active hills only - returns 409 HILL_COMPLETED on a
   * Done hill.
   */
  update: (id: string, body: UpdateHillBody) =>
    request(`/api/hills/${id}`, hillChartSchema, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /**
   * Atomic update of one or more of: x, color, completed. Active hills
   * only - returns 409 HILL_COMPLETED on a Done hill.
   */
  updateDot: (hillId: string, dotId: string, body: UpdateDotBody) =>
    request(`/api/hills/${hillId}/dots/${dotId}`, dotSchema, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

// -- Snapshots ---------------------------------------------------------------
export const snapshotsApi = {
  /** Snapshot history for a hill - newest first. */
  list: (hillId: string) => request(`/api/hills/${hillId}/snapshots`, snapshotsListResponseSchema),
  /**
   * Capture the current dot state as a regular snapshot. Active hills only
   * - returns 409 HILL_COMPLETED on a Done hill (the final snapshot is
   * already captured automatically at completion time).
   */
  create: (hillId: string, body: CreateSnapshotBody = {}) =>
    request(`/api/hills/${hillId}/snapshots`, snapshotSchema, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export { ApiError, request, API_URL };
